import { LiliNumberInput } from '@lili/lili-frontend-common';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ControllerWrapper } from '@Hooks/index';
import { DisplayField } from '@Components/DisplayField';
import type { OwnersCenterFormType } from '../OwnersCenterForm/OwnersCenterForm.types';
import type { ReactNode } from 'react';

export interface OwnerPercentageProps {
  ownerName: string;
  ownerIndex: number;
  errorMessage?: string;
  canEdit?: boolean;
  handleOnBlur?: (value: number | null) => void;
  onChangeValidate?: () => void;
  nameAccessoryComponent?: ReactNode;
  name: keyof OwnersCenterFormType;
}

export const OwnerPercentage = ({
  ownerName,
  ownerIndex,
  errorMessage,
  canEdit = true,
  handleOnBlur,
  onChangeValidate,
  nameAccessoryComponent,
  name,
}: OwnerPercentageProps) => {
  const { t } = useTranslation();
  const valueOnFocusRef = useRef<number | null>(null);

  return (
    <div className='flex w-full flex-col justify-start'>
      <div className='flex w-full items-start gap-[14px]'>
        <div className='w-full flex-1 basis-[70%]'>
          <DisplayField
            label={t('pages.ownersCenter.owner', { index: ownerIndex })}
            value={ownerName}
            disabled={!canEdit}
            accessoryComponent={nameAccessoryComponent}
            errorMessage={errorMessage}
            dataTestId={`owner-name-${ownerIndex}`}
          />
        </div>
        <div className='w-full flex-1 basis-[30%]'>
          <ControllerWrapper name={name}>
            {({ value, onChange, onBlur }) => (
              <LiliNumberInput
                onChange={(newValue: number | null) => {
                  onChange(newValue);
                  onChangeValidate?.();
                }}
                onFocus={() => {
                  valueOnFocusRef.current = value;
                }}
                id={name}
                title={t('pages.ownersCenter.ownership')}
                onBlur={() => {
                  onBlur();
                  if (value !== valueOnFocusRef.current) {
                    handleOnBlur?.(value);
                  }
                }}
                value={value}
                min={0}
                max={100}
                suffix='%'
                maxLength={3}
                maxFractionDigits={0}
                isPublic
              />
            )}
          </ControllerWrapper>
        </div>
      </div>
    </div>
  );
};
